(() => {
  'use strict';

  const VERIFIED_AT = '2026-09-18';
  const STORAGE_KEY = 'casabarista_espresso_finder_v1';
  const KLAVIYO_REVISION = '2026-07-15';

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
  const money = (amount) => { const value = Number(amount || 0); const hasCents = Math.abs(value - Math.round(value)) > .001; return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2 }).format(value); };
  const grinderDiscountRate = (root) => clamp((Number(root?.dataset?.grinderDiscountPercent || 10) || 0) / 100, 0, .5);
  const discountedGrinderPrice = (root, price) => Math.max(0, Number(price || 0) * (1 - grinderDiscountRate(root)));
  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  /*
   * Curated recommendation set, verified against CasaBarista.nl on 2026-09-18.
   * Live price, image, title and availability are refreshed from Shopify /products/{handle}.js.
   * Technical attributes below are intentionally centralized so they can be audited/edited in one place.
   */
  const MACHINES = [
    {
      handle: 'lelit-anna-pid-inox', brand: 'Lelit', name: 'Anna PID INOX', fallbackPrice: 519,
      sourceUrl: 'https://www.casabarista.nl/products/lelit-anna-pid-inox', verifiedAt: VERIFIED_AT,
      boiler: 'Single boiler', boilerKey: 'single', pump: 'Vibratiepomp', rotary: false, quietPump: false,
      pid: true, shotTimer: false, fixedWater: false, dualWater: false, volumetric: false, flowControl: false,
      width: 225, heatUpMinutes: null, simultaneous: false,
      scores: { espresso: 7.5, milk: 3, back: 3, ease: 6.5, control: 6, fast: 6, quiet: 4, compact: 10, fixedWater: 0, automation: 3, design: 5, traditional: 6, premium: 3 }
    },
    {
      handle: 'lelit-victoria', brand: 'Lelit', name: 'Victoria', fallbackPrice: 769,
      sourceUrl: 'https://www.casabarista.nl/products/lelit-victoria', verifiedAt: VERIFIED_AT,
      boiler: 'Single boiler', boilerKey: 'single', pump: 'Vibratiepomp', rotary: false, quietPump: false,
      pid: true, shotTimer: true, fixedWater: false, dualWater: false, volumetric: true, flowControl: false,
      width: 230, heatUpMinutes: null, simultaneous: false,
      scores: { espresso: 8.5, milk: 3.5, back: 3.5, ease: 8, control: 7.5, fast: 7, quiet: 4, compact: 9.5, fixedWater: 0, automation: 7.5, design: 5.5, traditional: 5, premium: 4.5 }
    },
    {
      handle: 'stone-lite', brand: 'Stone Espresso', name: 'STONE Lite', fallbackPrice: 1040,
      sourceUrl: 'https://www.casabarista.nl/products/stone-lite', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Vibratiepomp', rotary: false, quietPump: false,
      pid: false, pidLabel: 'Intern gestuurd', shotTimer: false, fixedWater: false, dualWater: false, volumetric: false, flowControl: false,
      width: 225, heatUpMinutes: 10, simultaneous: true,
      scores: { espresso: 7, milk: 8, back: 7, ease: 7, control: 4.5, fast: 10, quiet: 4, compact: 10, fixedWater: 0, automation: 3, design: 9, traditional: 6, premium: 5.5 }
    },
    {
      handle: 'lelit-marax', brand: 'Lelit', name: 'MaraX', fallbackPrice: 1090,
      sourceUrl: 'https://www.casabarista.nl/products/lelit-marax', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Stille vibratiepomp', rotary: false, quietPump: true,
      pid: true, shotTimer: false, fixedWater: false, dualWater: false, volumetric: false, flowControl: false,
      width: 225, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 8, milk: 8.5, back: 7.5, ease: 7, control: 6.5, fast: 5.5, quiet: 7.5, compact: 10, fixedWater: 0, automation: 3, design: 7, traditional: 8.5, premium: 6 }
    },
    {
      handle: 'lelit-elizabeth', brand: 'Lelit', name: 'Elizabeth', fallbackPrice: 1209,
      sourceUrl: 'https://www.casabarista.nl/products/lelit-elizabeth', verifiedAt: VERIFIED_AT,
      boiler: 'Dual boiler', boilerKey: 'dual', pump: 'Vibratiepomp', rotary: false, quietPump: false,
      pid: true, shotTimer: true, fixedWater: false, dualWater: false, volumetric: true, flowControl: false,
      width: 270, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 8.5, milk: 9, back: 8.5, ease: 9.5, control: 8.5, fast: 8, quiet: 4.5, compact: 8.5, fixedWater: 0, automation: 9, design: 5.5, traditional: 4.5, premium: 7 }
    },
    {
      handle: 'bezzera-bz-10spm', brand: 'Bezzera', name: 'BZ-10 SPM', fallbackPrice: 1240,
      sourceUrl: 'https://www.casabarista.nl/products/bezzera-bz-10spm', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Vibratiepomp', rotary: false, quietPump: false,
      pid: false, shotTimer: false, fixedWater: false, dualWater: false, volumetric: false, flowControl: false,
      width: 250, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 7.2, milk: 8.2, back: 7.2, ease: 6.8, control: 4.5, fast: 7.5, quiet: 4, compact: 9.2, fixedWater: 0, automation: 2, design: 6.5, traditional: 7.5, premium: 5.5 }
    },
    {
      handle: 'bezzera-aria-r-rotatiepomp-hx-pid', brand: 'Bezzera', name: 'Aria R', fallbackPrice: 1999,
      sourceUrl: 'https://www.casabarista.nl/products/bezzera-aria-r-rotatiepomp-hx-pid', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Rotatiepomp', rotary: true, quietPump: true,
      pid: true, shotTimer: true, fixedWater: true, dualWater: true, volumetric: false, flowControl: false,
      width: 250, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 8.4, milk: 8.8, back: 8.4, ease: 7.8, control: 7.7, fast: 6.5, quiet: 9.5, compact: 9.2, fixedWater: 10, automation: 5.5, design: 8.5, traditional: 8.5, premium: 8 }
    },
    {
      handle: 'eureka-costanza-chrome', brand: 'Eureka', name: 'Costanza V', fallbackPrice: 1300,
      sourceUrl: 'https://www.casabarista.nl/products/eureka-costanza-chrome', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Vibratiepomp', rotary: false, quietPump: true,
      pid: true, shotTimer: true, fixedWater: false, dualWater: false, volumetric: false, flowControl: false,
      width: 301, heatUpMinutes: 13, simultaneous: true,
      scores: { espresso: 8, milk: 8.3, back: 7.5, ease: 7.5, control: 7, fast: 9, quiet: 6.5, compact: 6.8, fixedWater: 0, automation: 5.5, design: 8.5, traditional: 8, premium: 6.5 }
    },
    {
      handle: 'rocket-appartamento-tca', brand: 'Rocket Espresso', name: 'Appartamento TCA', fallbackPrice: 1510,
      sourceUrl: 'https://www.casabarista.nl/products/rocket-appartamento-tca', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Vibratiepomp', rotary: false, quietPump: false,
      pid: true, pidLabel: 'Temperatuurstanden', shotTimer: false, fixedWater: false, dualWater: false, volumetric: false, flowControl: false,
      width: 270, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 8, milk: 8.5, back: 7.5, ease: 6.8, control: 6.5, fast: 6, quiet: 4, compact: 8.5, fixedWater: 0, automation: 2.5, design: 9.5, traditional: 9, premium: 7 }
    },
    {
      handle: 'bezzera-luce-s-pid', brand: 'Bezzera', name: 'Luce S PID', fallbackPrice: 1569,
      sourceUrl: 'https://www.casabarista.nl/products/bezzera-luce-s-pid', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Vibratiepomp', rotary: false, quietPump: false,
      pid: true, shotTimer: true, fixedWater: false, dualWater: false, volumetric: false, flowControl: false,
      width: 305, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 8.2, milk: 8.6, back: 7.8, ease: 7.2, control: 7.5, fast: 6, quiet: 4, compact: 6.4, fixedWater: 0, automation: 4.5, design: 8, traditional: 8.8, premium: 7 }
    },
    {
      handle: 'eureka-costanza-r-chrome', brand: 'Eureka', name: 'Costanza R', fallbackPrice: 1999,
      sourceUrl: 'https://www.casabarista.nl/products/eureka-costanza-r-chrome', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Rotatiepomp', rotary: true, quietPump: true,
      pid: true, shotTimer: true, fixedWater: false, dualWater: false, volumetric: false, flowControl: false,
      width: 301, heatUpMinutes: 13, simultaneous: true,
      scores: { espresso: 8.5, milk: 8.8, back: 8.2, ease: 7.5, control: 7.7, fast: 9, quiet: 9.5, compact: 6.8, fixedWater: 0, automation: 5.5, design: 8.7, traditional: 8.5, premium: 7.8 }
    },
    {
      handle: 'bezzera-sole-pid', brand: 'Bezzera', name: 'Sole PID', fallbackPrice: 1999,
      sourceUrl: 'https://www.casabarista.nl/products/bezzera-sole-pid', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Rotatiepomp', rotary: true, quietPump: true,
      pid: true, shotTimer: true, fixedWater: true, dualWater: true, volumetric: false, flowControl: false,
      width: 305, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 8.5, milk: 9, back: 8.8, ease: 7.5, control: 7.8, fast: 6, quiet: 9.5, compact: 6.5, fixedWater: 10, automation: 5, design: 8, traditional: 8.8, premium: 8 }
    },
    {
      handle: 'rocket-mozzafiato-r-fast', brand: 'Rocket Espresso', name: 'Mozzafiato R Fast', fallbackPrice: 2190,
      sourceUrl: 'https://www.casabarista.nl/products/rocket-mozzafiato-r-fast', verifiedAt: VERIFIED_AT,
      boiler: 'Warmtewisselaar', boilerKey: 'hx', pump: 'Rotatiepomp', rotary: true, quietPump: true,
      pid: true, shotTimer: false, fixedWater: true, dualWater: true, volumetric: false, flowControl: false,
      width: 310, heatUpMinutes: 10, simultaneous: true,
      scores: { espresso: 8.6, milk: 9, back: 9, ease: 7.5, control: 7.5, fast: 10, quiet: 9.5, compact: 6, fixedWater: 10, automation: 4.5, design: 9.2, traditional: 9, premium: 8.5 }
    },
    {
      handle: 'lelit-bianca-v3', brand: 'Lelit', name: 'Bianca V3', fallbackPrice: 2074,
      sourceUrl: 'https://www.casabarista.nl/products/lelit-bianca-v3', verifiedAt: VERIFIED_AT,
      boiler: 'Dual boiler', boilerKey: 'dual', pump: 'Rotatiepomp', rotary: true, quietPump: true,
      pid: true, shotTimer: true, fixedWater: true, dualWater: true, volumetric: false, flowControl: true,
      width: 290, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 10, milk: 9.5, back: 9.2, ease: 7, control: 10, fast: 6, quiet: 9.5, compact: 7.5, fixedWater: 10, automation: 6, design: 8.5, traditional: 9.5, premium: 9 }
    },
    {
      handle: 'bezzera-duo-top', brand: 'Bezzera', name: 'Duo TOP MN', fallbackPrice: 2308,
      sourceUrl: 'https://www.casabarista.nl/products/bezzera-duo-top', verifiedAt: VERIFIED_AT,
      boiler: 'Dual boiler', boilerKey: 'dual', pump: 'Rotatiepomp', rotary: true, quietPump: true,
      pid: true, shotTimer: true, fixedWater: true, dualWater: true, volumetric: false, flowControl: false,
      width: 310, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 9.3, milk: 9.5, back: 9.5, ease: 9, control: 9.2, fast: 7, quiet: 9.5, compact: 6, fixedWater: 10, automation: 9, design: 8, traditional: 8.5, premium: 9 }
    },
    {
      handle: 'bezzera-duo-top-de', brand: 'Bezzera', name: 'Duo TOP DE', fallbackPrice: 2516,
      sourceUrl: 'https://www.casabarista.nl/products/bezzera-duo-top-de', verifiedAt: VERIFIED_AT,
      boiler: 'Dual boiler', boilerKey: 'dual', pump: 'Rotatiepomp', rotary: true, quietPump: true,
      pid: true, shotTimer: true, fixedWater: true, dualWater: true, volumetric: true, flowControl: false,
      width: 310, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 9.2, milk: 9.6, back: 9.5, ease: 10, control: 8.8, fast: 7, quiet: 9.5, compact: 6, fixedWater: 10, automation: 10, design: 8, traditional: 5.5, premium: 9.2 }
    },
    {
      handle: 'rocket-r58-tune', brand: 'Rocket Espresso', name: 'R58 Tune', fallbackPrice: 3190,
      sourceUrl: 'https://www.casabarista.nl/products/rocket-r58-tune', verifiedAt: VERIFIED_AT,
      boiler: 'Dual boiler', boilerKey: 'dual', pump: 'Rotatiepomp', rotary: true, quietPump: true,
      pid: true, shotTimer: false, fixedWater: true, dualWater: true, volumetric: false, flowControl: true,
      width: 335, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 9.8, milk: 9.7, back: 9.6, ease: 7.5, control: 10, fast: 6.5, quiet: 9.5, compact: 5, fixedWater: 10, automation: 6.5, design: 9.5, traditional: 9.5, premium: 9.7 }
    },
    {
      handle: 'mahlkonig-xenia-zwart', brand: 'Mahlkönig', name: 'Xenia Brew-by-Sync', fallbackPrice: 2999,
      sourceUrl: 'https://www.casabarista.nl/products/mahlkonig-xenia-zwart', verifiedAt: VERIFIED_AT,
      boiler: 'Dual boiler', boilerKey: 'dual', pump: 'Pomp niet gespecificeerd', rotary: false, quietPump: false,
      pid: true, pidLabel: 'Triple PID', shotTimer: false, fixedWater: false, dualWater: false, volumetric: false, flowControl: false, weightBased: true,
      width: 270, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 9.7, milk: 9.2, back: 8.8, ease: 9.8, control: 9.4, fast: 8, quiet: 5, compact: 8.5, fixedWater: 0, automation: 10, design: 9, traditional: 3.5, premium: 9.4 }
    },
    {
      handle: 'la-marzocco-linea-mini-r', brand: 'La Marzocco', name: 'Linea Mini R', fallbackPrice: 5264,
      sourceUrl: 'https://www.casabarista.nl/products/la-marzocco-linea-mini-r', verifiedAt: VERIFIED_AT,
      boiler: 'Dual boiler', boilerKey: 'dual', pump: 'Rotatiepomp', rotary: true, quietPump: true,
      pid: true, shotTimer: false, fixedWater: true, dualWater: true, volumetric: false, flowControl: false,
      width: 355, heatUpMinutes: null, simultaneous: true,
      scores: { espresso: 9.6, milk: 10, back: 10, ease: 8.5, control: 9, fast: 8, quiet: 9.5, compact: 4.5, fixedWater: 10, automation: 8, design: 9.8, traditional: 7.5, premium: 10 }
    }
  ];

  const GRINDERS = [
    { handle: 'eureka-mignon-manuale', brand: 'Eureka', name: 'Mignon Manuale', fallbackPrice: 250, mode: 'hopper', quality: 5, easy: 5, singleDose: 1, gbw: 0, sourceUrl: 'https://www.casabarista.nl/products/eureka-mignon-manuale' },
    { handle: 'bezzera-bb005-timer', brand: 'Bezzera', name: 'BB005 Timer', fallbackPrice: 321, mode: 'hopper', quality: 5.5, easy: 6.5, singleDose: 1, gbw: 0, sourceUrl: 'https://www.casabarista.nl/products/bezzera-bb005-timer' },
    { handle: 'lx-newton-55', brand: 'LX', name: 'Newton 55', fallbackPrice: 409, mode: 'hopper', quality: 6.7, easy: 8, singleDose: 1, gbw: 0, sourceUrl: 'https://www.casabarista.nl/products/lx-newton-55' },
    { handle: 'eureka-mignon-specialita', brand: 'Eureka', name: 'Mignon Specialità', fallbackPrice: 460, mode: 'hopper', quality: 7.5, easy: 8.5, singleDose: 2, gbw: 0, sourceUrl: 'https://www.casabarista.nl/products/eureka-mignon-specialita' },
    { handle: 'mahlkonig-x54-zwart', brand: 'Mahlkönig', name: 'X54', fallbackPrice: 449, mode: 'hopper', quality: 7.7, easy: 8.5, singleDose: 2, gbw: 0, sourceUrl: 'https://www.casabarista.nl/products/mahlkonig-x54-zwart' },
    { handle: 'lx-edison-65', brand: 'LX', name: 'Edison 65', fallbackPrice: 549, mode: 'hopper', quality: 8.2, easy: 8.5, singleDose: 2, gbw: 0, sourceUrl: 'https://www.casabarista.nl/products/lx-edison-65' },
    { handle: 'mahlkonig-x64-sd-zwart', brand: 'Mahlkönig', name: 'X64 SD', fallbackPrice: 449, mode: 'single', quality: 8.1, easy: 7, singleDose: 10, gbw: 0, sourceUrl: 'https://www.casabarista.nl/products/mahlkonig-x64-sd-zwart' },
    { handle: 'eureka-mignon-single-dose-pro', brand: 'Eureka', name: 'Mignon Single Dose Pro', fallbackPrice: 649, mode: 'single', quality: 8.6, easy: 7.5, singleDose: 10, gbw: 0, sourceUrl: 'https://www.casabarista.nl/products/eureka-mignon-single-dose-pro' },
    { handle: 'eureka-mignon-libra-65-all-purpose-gbw', brand: 'Eureka', name: 'Mignon Libra 65 All Purpose GbW', fallbackPrice: 739, mode: 'gbw', quality: 8.7, easy: 9.7, singleDose: 1, gbw: 10, sourceUrl: 'https://www.casabarista.nl/products/eureka-mignon-libra-65-all-purpose-gbw' },
    { handle: 'fiorenzato-allground-sense-gbw', brand: 'Fiorenzato', name: 'AllGround Sense GbW', fallbackPrice: 950, mode: 'gbw', quality: 9.2, easy: 9.7, singleDose: 1, gbw: 10, sourceUrl: 'https://www.casabarista.nl/products/fiorenzato-allground-sense-gbw' },
    { handle: 'mahlkonig-e-64-ws-zwart', brand: 'Mahlkönig', name: 'E64 WS', fallbackPrice: 949, mode: 'gbw', quality: 9.6, easy: 9.8, singleDose: 1, gbw: 10, sourceUrl: 'https://www.casabarista.nl/products/mahlkonig-e-64-ws-zwart' }
  ];

  const LABELS = {
    currentSetup: {
      none: 'Ik begin helemaal vanaf nul', good_grinder: 'Ik heb al een goede espressomolen',
      upgrade: 'Ik heb al een complete setup en zoek een betere machine', machine_only: 'Ik heb al een machine, maar nog geen goede molen',
      unsure: 'Ik weet niet of mijn huidige molen geschikt is'
    },
    drinks: { espresso: 'Vooral espresso', black: 'Vooral zwarte koffie / americano', mix: 'Espresso én melkdranken', milk: 'Vooral cappuccino, flat white en latte', all: 'Van alles wat' },
    batch: { one: 'Meestal één', two: 'Meestal twee', three_four: 'Regelmatig drie of vier', five_plus: 'Vaak vijf of meer', guests: 'Ik ontvang regelmatig mensen' },
    workflow: { easy: 'Vooral makkelijk en consistent', barista: 'Een beetje barista spelen', tune: 'Ik wil kunnen finetunen', hobby: 'Koffie is echt een hobby' },
    priorities: { fast: 'Snel klaar voor gebruik', quiet: 'Zo stil mogelijk', compact: 'Compact formaat', control: 'Veel instelmogelijkheden', ease: 'Zo makkelijk mogelijk', fixedWater: 'Vaste wateraansluiting', design: 'Design en uitstraling', milk: 'Sterke prestaties voor melkdranken' },
    width: { '250': 'Maximaal ongeveer 25 cm', '300': 'Maximaal ongeveer 30 cm', '350': 'Maximaal ongeveer 35 cm', unlimited: 'Ruimte is geen probleem' },
    automation: { manual: 'Ik doe het graag handmatig', helpful: 'Handige functies zijn mooi meegenomen', automated: 'Ik wil dat de machine stappen makkelijker maakt', ease_first: 'Gebruiksgemak is belangrijker dan maximale controle' },
    grinder: { easy: 'Zo makkelijk mogelijk', single: 'Ik wissel graag tussen verschillende bonen', gbw: 'Zo automatisch en consistent mogelijk', advise: 'Geen voorkeur, adviseer mij' }
  };

  const BASE_QUESTIONS = {
    currentSetup: {
      id: 'currentSetup', title: 'Hoe ziet je huidige koffie-opstelling eruit?',
      help: 'Zo stemmen we het advies af op wat je al hebt en wat je nog nodig hebt.',
      options: [
        ['none', 'Ik begin helemaal vanaf nul', 'Ik heb nog geen geschikte espressomachine of espressomolen.'],
        ['good_grinder', 'Ik heb al een goede espressomolen', 'Mijn huidige molen is geschikt voor espresso.'],
        ['upgrade', 'Ik heb al een complete setup', 'Mijn molen blijft en ik zoek vooral een betere machine.'],
        ['machine_only', 'Ik heb al een machine, maar nog geen goede molen', 'Ik wil ook een passende espressomolen erbij.'],
        ['unsure', 'Ik weet niet of mijn molen geschikt is', 'Neem voor de zekerheid ook een passende molen mee.']
      ]
    },
    drinks: {
      id: 'drinks', title: 'Welke koffie drink je het vaakst?',
      help: 'Je favoriete koffies bepalen welke machine in de praktijk het prettigst voor je werkt.',
      options: [
        ['espresso', 'Vooral espresso', 'Ik wil zoveel mogelijk smaak en controle uit mijn espresso halen.'],
        ['black', 'Vooral zwarte koffie / americano', 'Espresso en americano, meestal zonder melk.'],
        ['mix', 'Espresso én melkdranken', 'Ik wil makkelijk kunnen wisselen tussen zwart en melkdranken.'],
        ['milk', 'Vooral cappuccino, flat white en latte', 'Cappuccino, flat white en latte zijn mijn favorieten.'],
        ['all', 'Van alles wat', 'Ik wissel graag af tussen verschillende koffies.']
      ]
    },
    batch: {
      id: 'batch', title: 'Hoeveel koffies maak je meestal achter elkaar?',
      help: 'Meerdere koffies direct achter elkaar vragen meer van een machine dan een paar verspreid over de dag.',
      options: [
        ['one', 'Meestal één', 'Vaak zet ik alleen voor mezelf.'],
        ['two', 'Meestal twee', 'Bijvoorbeeld twee koffies bij het ontbijt.'],
        ['three_four', 'Regelmatig drie of vier', 'De machine moet vlot door kunnen werken.'],
        ['five_plus', 'Vaak vijf of meer', 'Ik wil meerdere koffies vlot achter elkaar kunnen zetten.'],
        ['guests', 'Ik ontvang regelmatig mensen', 'Ik wil zonder gedoe voor meerdere mensen koffie maken.']
      ]
    },
    workflow: {
      id: 'workflow', title: 'Hoe wil je het liefst koffiezetten?',
      help: 'Kies wat het beste past bij hoeveel je zelf wilt instellen en experimenteren.',
      options: [
        ['easy', 'Vooral makkelijk en consistent', 'Iedere dag goede koffie zonder veel instellingen te veranderen.'],
        ['barista', 'Een beetje barista spelen', 'Malen, tampen en handmatig zetten vind ik leuk, maar het moet overzichtelijk blijven.'],
        ['tune', 'Ik wil kunnen finetunen', 'Ik wil temperatuur en andere instellingen naar smaak kunnen aanpassen.'],
        ['hobby', 'Koffie is echt een hobby', 'Ik wil veel controle en graag experimenteren met extractie en bonen.']
      ]
    },
    priorities: {
      id: 'priorities', title: 'Wat vind je daarnaast het belangrijkst?',
      help: 'Kies maximaal twee punten die voor jou echt het verschil maken.', multi: true, max: 2,
      options: [
        ['fast', 'Snel klaar voor gebruik', 'Ik wil niet lang wachten voordat ik koffie kan zetten.'],
        ['quiet', 'Zo stil mogelijk', 'De machine mag tijdens het zetten zo weinig mogelijk geluid maken.'],
        ['compact', 'Compact formaat', 'De machine moet weinig werkbladruimte innemen.'],
        ['control', 'Veel instelmogelijkheden', 'Ik wil mijn espresso precies naar smaak kunnen afstellen.'],
        ['ease', 'Zo makkelijk mogelijk', 'Ik wil vooral een prettige en voorspelbare workflow.'],
        ['fixedWater', 'Vaste wateraansluiting', 'Ik wil de machine rechtstreeks op de waterleiding kunnen aansluiten.'],
        ['design', 'Design en uitstraling', 'De machine mag echt een blikvanger zijn.'],
        ['milk', 'Sterke prestaties voor melkdranken', 'Ik wil snel en krachtig melk kunnen opschuimen.']
      ]
    },
    width: {
      id: 'width', title: 'Hoeveel ruimte heb je ongeveer in de breedte?',
      help: 'Dan laten we alleen machines zien die binnen die ruimte passen.',
      options: [
        ['250', 'Maximaal ongeveer 25 cm', 'Extra compact.'],
        ['300', 'Maximaal ongeveer 30 cm', 'Past op een kleiner werkblad.'],
        ['350', 'Maximaal ongeveer 35 cm', 'De meeste thuismodellen passen.'],
        ['unlimited', 'Ruimte is geen probleem', 'Afmetingen hoeven niet mee te wegen.']
      ]
    },
    automation: {
      id: 'automation', title: 'Hoe belangrijk is extra gebruiksgemak voor jou?',
      help: 'Denk aan functies die je helpen om je espresso makkelijker en consistenter te zetten.',
      options: [
        ['manual', 'Niet belangrijk', 'Ik vind een volledig handmatige workflow juist leuk.'],
        ['helpful', 'Mooi meegenomen', 'Een paar functies die me helpen zijn welkom.'],
        ['automated', 'Graag', 'Ik wil dat de machine bepaalde stappen eenvoudiger en consistenter maakt.'],
        ['ease_first', 'Heel belangrijk', 'Gemak en herhaalbaarheid zijn voor mij belangrijker dan maximale controle.']
      ]
    },
    grinder: {
      id: 'grinder', title: 'Hoe wil je je koffie het liefst malen?',
      help: 'Kies wat het beste past bij je dagelijkse routine.',
      options: [
        ['easy', 'Zo makkelijk mogelijk', 'Bonen in het reservoir en iedere dag snel dezelfde dosis.'],
        ['single', 'Ik wissel graag tussen verschillende bonen', 'Ik weeg per shot af en wil makkelijk tussen bonen kunnen wisselen.'],
        ['gbw', 'Zo automatisch en consistent mogelijk', 'Bij voorkeur maalt de molen automatisch op gewicht.'],
        ['advise', 'Geen voorkeur', 'Kies voor mij de beste balans tussen molen, machine en budget.']
      ]
    }
  };

  function needsGrinder(answers) {
    return ['none', 'machine_only', 'unsure'].includes(answers.currentSetup);
  }

  function getBudgetQuestion(answers) {
    const withGrinder = needsGrinder(answers);
    return {
      id: 'budget',
      title: withGrinder ? 'Wat wil je ongeveer uitgeven aan je complete setup?' : 'Wat wil je ongeveer aan de espressomachine uitgeven?',
      help: withGrinder
        ? 'Houd rekening met de espressomachine, een passende molen en de belangrijkste basisaccessoires.'
        : 'Kies het budget waar je je prettig bij voelt. Een iets duurder alternatief kan worden getoond als het echt iets toevoegt.',
      options: withGrinder ? [
        ['1500', 'Tot €1.500', 'Machine + molen + basisaccessoires.'],
        ['2000', '€1.500 – €2.000', 'Een sterke complete thuissetup.'],
        ['2750', '€2.000 – €2.750', 'Meer ruimte voor machine én molen.'],
        ['4000', '€2.750 – €4.000', 'Premium thuisbarista-setup.'],
        ['6000', '€4.000 – €6.000', 'High-end machine en molen.'],
        ['99999', '€6.000+', 'Budget is niet de belangrijkste beperking.']
      ] : [
        ['1200', 'Tot €1.200', 'Compacte en complete machines binnen dit budget.'],
        ['1700', '€1.200 – €1.700', 'Meer keuze in krachtige allround machines.'],
        ['2300', '€1.700 – €2.300', 'Meer controle, capaciteit en afwerkingsniveau.'],
        ['3500', '€2.300 – €3.500', 'Ruimte voor uitgebreide high-end machines.'],
        ['5000', '€3.500 – €5.000', 'Premium segment.'],
        ['99999', '€5.000+', 'Budget is niet de belangrijkste beperking.']
      ]
    };
  }

  function questionSequence(answers) {
    const sequence = [BASE_QUESTIONS.currentSetup, BASE_QUESTIONS.drinks, BASE_QUESTIONS.batch, BASE_QUESTIONS.workflow, BASE_QUESTIONS.priorities];
    if ((answers.priorities || []).includes('compact')) sequence.push(BASE_QUESTIONS.width);
    sequence.push(BASE_QUESTIONS.automation);
    if (needsGrinder(answers)) sequence.push(BASE_QUESTIONS.grinder);
    sequence.push(getBudgetQuestion(answers));
    return sequence;
  }

  function loadSavedAnswers() {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) { return {}; }
  }

  function saveAnswers(answers) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(answers)); } catch (_) {}
  }

  async function fetchShopifyProduct(handle, fallback) {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
      if (response.status === 404) return { ...fallback, exists: false };
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const product = await response.json();
      const variants = (Array.isArray(product.variants) ? product.variants : []).map((variant) => ({
        id: Number(variant.id),
        title: variant.title || 'Standaard',
        available: variant.available !== false,
        price: Number(variant.price || 0) / 100,
        options: Array.isArray(variant.options) ? variant.options : []
      }));
      const availableVariants = variants.filter((v) => v.available);
      const pricePool = availableVariants.length ? availableVariants : variants;
      const prices = pricePool.map((v) => Number(v.price || 0)).filter((n) => n > 0);
      const livePrice = prices.length ? Math.min(...prices) : fallback.fallbackPrice;
      const defaultVariant = availableVariants[0] || variants[0] || null;
      return {
        ...fallback,
        exists: true,
        title: product.title || `${fallback.brand} ${fallback.name}`,
        price: livePrice,
        available: product.available !== false,
        image: product.featured_image || (product.images && product.images[0]) || '',
        url: product.url || `/products/${handle}`,
        variants,
        defaultVariantId: defaultVariant ? defaultVariant.id : null,
        defaultVariantPrice: defaultVariant && defaultVariant.price ? defaultVariant.price : livePrice
      };
    } catch (error) {
      return {
        ...fallback,
        exists: true,
        title: `${fallback.brand} ${fallback.name}`,
        price: fallback.fallbackPrice,
        available: null,
        image: '',
        url: `/products/${handle}`,
        variants: [],
        defaultVariantId: null,
        defaultVariantPrice: fallback.fallbackPrice,
        liveFetchFailed: true
      };
    }
  }

  function addWeighted(scoreObj, key, weight, scoreState) {
    const value = Number(scoreObj[key] || 0);
    scoreState.score += value * weight;
    scoreState.possible += 10 * Math.abs(weight);
  }

  function chooseGrinder(machine, grinders, answers, budgetMax, accessoryReserve, discountRate = .10) {
    if (!needsGrinder(answers)) return null;
    const preference = answers.grinder || 'advise';
    const machinePrice = machine.price || machine.fallbackPrice;
    const targetQuality = clamp(5.2 + (machinePrice / 1250), 5.5, 9.7);

    let best = null;
    grinders.filter((g) => g.exists !== false).forEach((grinder) => {
      const grinderPrice = grinder.price || grinder.fallbackPrice;
      let preferenceScore = 0;
      if (preference === 'single') preferenceScore = grinder.singleDose * 4;
      else if (preference === 'gbw') preferenceScore = grinder.gbw * 4;
      else if (preference === 'easy') preferenceScore = grinder.easy * 4;
      else preferenceScore = grinder.easy * 1.4 + grinder.quality * 1.6;

      const qualityFit = 18 - Math.abs(grinder.quality - targetQuality) * 3;
      const syncBonus = machine.handle === 'mahlkonig-xenia-zwart' && grinder.handle === 'mahlkonig-e-64-ws-zwart' ? 24 : 0;
      const discountedPrice = grinderPrice * (1 - discountRate);
      const total = machinePrice + discountedPrice + accessoryReserve;
      const ratio = budgetMax >= 90000 ? 0 : Math.max(0, (total - budgetMax) / budgetMax);
      const budgetPenalty = ratio === 0 ? 0 : ratio <= .05 ? 6 : ratio <= .10 ? 18 : 55 + ratio * 50;
      const unavailablePenalty = grinder.available === false ? 10 : 0;
      const score = preferenceScore + qualityFit + syncBonus - budgetPenalty - unavailablePenalty;

      if (!best || score > best.score) best = { grinder, score, total, regularPrice: grinderPrice, discountedPrice, savings: grinderPrice - discountedPrice };
    });
    return best;
  }

  function scoreMachine(machine, answers, grinderChoice, budgetMax, accessoryReserve) {
    const s = { score: 0, possible: 0, excluded: false, flags: [] };
    const d = machine.scores;

    if (machine.exists === false) return { ...s, excluded: true, reason: 'product_removed' };

    const priorities = answers.priorities || [];
    const widthLimit = answers.width && answers.width !== 'unlimited' ? Number(answers.width) : null;
    if (priorities.includes('compact') && widthLimit && machine.width && machine.width > widthLimit) {
      return { ...s, excluded: true, reason: 'too_wide' };
    }
    if (priorities.includes('fixedWater') && !machine.fixedWater) {
      return { ...s, excluded: true, reason: 'no_fixed_water' };
    }

    const drinkWeights = {
      espresso: { espresso: 3, control: .6 },
      black: { espresso: 2.5, ease: .5 },
      mix: { espresso: 1.35, milk: 1.6, ease: .35 },
      milk: { milk: 2.7, back: .8, ease: .3 },
      all: { espresso: 1, milk: 1.25, ease: .7, back: .45 }
    }[answers.drinks] || { espresso: 1, milk: 1 };
    Object.entries(drinkWeights).forEach(([k, w]) => addWeighted(d, k, w, s));

    const batchWeights = {
      one: { ease: .7, compact: .5 },
      two: { ease: .65, back: .55 },
      three_four: { back: 1.9, milk: answers.drinks === 'milk' || answers.drinks === 'mix' ? .5 : 0 },
      five_plus: { back: 2.8, milk: answers.drinks === 'milk' || answers.drinks === 'mix' ? .8 : 0 },
      guests: { back: 3, ease: .6, milk: answers.drinks === 'milk' || answers.drinks === 'mix' ? .8 : 0 }
    }[answers.batch] || { back: 1 };
    Object.entries(batchWeights).forEach(([k, w]) => w && addWeighted(d, k, w, s));

    const workflowWeights = {
      easy: { ease: 2.1, automation: 1.25 },
      barista: { traditional: 1.8, ease: .4, control: .6 },
      tune: { control: 2.6, espresso: .5 },
      hobby: { control: 3, traditional: .8, premium: .35 }
    }[answers.workflow] || { ease: 1 };
    Object.entries(workflowWeights).forEach(([k, w]) => addWeighted(d, k, w, s));

    priorities.forEach((priority) => {
      const map = { fast: 'fast', quiet: 'quiet', compact: 'compact', control: 'control', ease: 'ease', fixedWater: 'fixedWater', design: 'design', milk: 'milk' };
      if (map[priority]) addWeighted(d, map[priority], 2.45, s);
    });

    const automationWeights = {
      manual: { traditional: 1.5, control: .35 },
      helpful: { ease: .8, automation: .7 },
      automated: { automation: 1.8, ease: .8 },
      ease_first: { ease: 2, automation: 1.4 }
    }[answers.automation] || { ease: .5 };
    Object.entries(automationWeights).forEach(([k, w]) => addWeighted(d, k, w, s));

    const machinePrice = machine.price || machine.fallbackPrice;
    const total = needsGrinder(answers)
      ? (grinderChoice ? grinderChoice.total : machinePrice + accessoryReserve)
      : machinePrice;
    const over = budgetMax >= 90000 ? 0 : (total - budgetMax) / budgetMax;
    s.possible += 12;
    if (over <= 0) s.score += 12;
    else if (over <= .05) { s.score += 7; s.flags.push('slightly_over_budget'); }
    else if (over <= .10) { s.score -= 4; s.flags.push('over_budget'); }
    else if (over <= .20) { s.score -= 24; s.flags.push('well_over_budget'); }
    else { s.score -= 55; s.flags.push('far_over_budget'); }

    if (machine.available === false) s.score -= 7;
    const ratio = s.possible ? s.score / s.possible : 0;
    const matchPercent = Math.round(clamp(55 + ratio * 43, 55, 98));
    return { ...s, total, matchPercent };
  }

  function machineReasonCandidates(machine, answers, scoreResult, grinderChoice, budgetMax) {
    const reasons = [];
    const priorities = answers.priorities || [];
    const add = (priority, text) => reasons.push({ priority, text });

    if (['milk', 'mix', 'all'].includes(answers.drinks) && machine.simultaneous && ['hx', 'dual'].includes(machine.boilerKey)) {
      add(9, `Je drinkt regelmatig melkdranken. De ${machine.boilerKey === 'dual' ? 'dual boiler' : 'warmtewisselaar'} laat je espresso zetten en melk opschuimen zonder eerst tussen zet- en stoomtemperatuur te wisselen.`);
    }
    if (['five_plus', 'guests', 'three_four'].includes(answers.batch) && machine.scores.back >= 8.5) {
      add(9, 'Je maakt regelmatig meerdere koffies achter elkaar. Deze machine heeft genoeg capaciteit om vlot door te werken.');
    }
    if (priorities.includes('quiet') && machine.rotary) {
      add(10, 'Je gaf aan dat stilte belangrijk is. De rotatiepomp werkt rustiger dan een traditionele vibratiepomp.');
    } else if (priorities.includes('quiet') && machine.quietPump) {
      add(8, 'Je gaf aan dat stilte belangrijk is. Dit model gebruikt een stillere pompconfiguratie dan veel traditionele vibratiepompen.');
    }
    if (priorities.includes('fast') && machine.heatUpMinutes) {
      add(10, `Je wilt snel kunnen beginnen. Dit model is in ongeveer ${machine.heatUpMinutes === 13 ? '10–15' : machine.heatUpMinutes} minuten op temperatuur.`);
    }
    if (priorities.includes('compact') && machine.width) {
      add(10, `Je hebt een compact formaat als prioriteit gekozen. Deze machine is ongeveer ${(machine.width / 10).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} cm breed.`);
    }
    if (priorities.includes('fixedWater') && machine.fixedWater) {
      add(10, 'Je wilt een vaste wateraansluiting. Dit model kan direct op de waterleiding worden aangesloten.');
    }
    if ((answers.workflow === 'tune' || answers.workflow === 'hobby' || priorities.includes('control')) && machine.flowControl) {
      add(10, 'Je wilt veel kunnen finetunen. Met flow control kun je tijdens de extractie ook de waterflow beïnvloeden.');
    } else if ((answers.workflow === 'tune' || answers.workflow === 'hobby' || priorities.includes('control')) && machine.pid) {
      add(8, 'Je wilt kunnen finetunen. De instelbare temperatuurregeling geeft je meer controle over verschillende bonen en recepten.');
    }
    if (['helpful', 'automated', 'ease_first'].includes(answers.automation) && machine.weightBased) {
      add(10, 'Je vindt herhaalbaarheid en gemak belangrijk. De geïntegreerde weegschaal kan de extractie automatisch stoppen zodra je ingestelde doelgewicht is bereikt.');
    }
    if (['automated', 'ease_first'].includes(answers.automation) && machine.volumetric) {
      add(10, 'Gebruiksgemak weegt voor jou zwaar. De programmeerbare dosering helpt je shots makkelijker en consistenter te herhalen.');
    } else if (['helpful', 'automated', 'ease_first'].includes(answers.automation) && machine.shotTimer) {
      add(7, 'Je vindt handige functies prettig. De ingebouwde shot timer maakt het eenvoudiger om je extractietijd te controleren.');
    }
    if (['espresso', 'black'].includes(answers.drinks) && machine.pid) {
      add(7, 'Je drinkt vooral espresso of zwarte koffie. De temperatuurregeling helpt je de extractie beter op je koffieboon af te stemmen.');
    }
    if (priorities.includes('design')) {
      const style = machine.boilerKey === 'dual' && machine.brand === 'La Marzocco' ? 'professionele La Marzocco-opbouw' : machine.name.includes('STONE') ? 'compacte, moderne behuizing' : 'klassieke premium espressomachine-uitstraling';
      add(5, `Design is belangrijk voor je. Dit model heeft een ${style}.`);
    }
    if (scoreResult.total <= budgetMax || budgetMax >= 90000) {
      add(6, needsGrinder(answers) && grinderChoice
        ? 'Deze combinatie van machine en molen past binnen het budget dat je hebt aangegeven.'
        : 'Deze machine past binnen het budget dat je hebt aangegeven.');
    }

    if (!reasons.length) add(1, 'Deze machine sluit goed aan op je koffiestijl, gewenste gebruiksgemak en budget.');
    return reasons.sort((a, b) => b.priority - a.priority).slice(0, 3).map((r) => r.text);
  }

  function grinderReason(grinder, answers) {
    if (!grinder) return '';
    const pref = answers.grinder || 'advise';
    if (pref === 'single' && grinder.singleDose >= 8) return 'Je wilt graag tussen bonen wisselen. Deze single-dose molen is juist bedoeld om per shot af te wegen en snel van boon te wisselen.';
    if (pref === 'gbw' && grinder.gbw >= 8) return 'Je koos voor maximale consistentie en gemak. Deze molen doseert op gewicht, waardoor je minder handmatig hoeft af te wegen.';
    if (pref === 'easy') return 'Je wilt een eenvoudige dagelijkse workflow. Deze molen werkt met een bonenreservoir en is gericht op snel en herhaalbaar malen.';
    return 'Deze molen biedt een goede balans tussen maalkwaliteit, gebruiksgemak en het niveau van de geadviseerde machine.';
  }

  function tradeoffLabel(best, alt) {
    const bestPrice = best.machine.price || best.machine.fallbackPrice;
    const altPrice = alt.machine.price || alt.machine.fallbackPrice;
    if (altPrice < bestPrice - 180) return 'Voordeliger alternatief';
    if ((alt.machine.scores.ease - best.machine.scores.ease) >= 1.5) return 'Meer gebruiksgemak';
    if ((alt.machine.scores.control - best.machine.scores.control) >= 1.5) return 'Meer controle';
    if ((alt.machine.scores.fast - best.machine.scores.fast) >= 1.8) return 'Sneller klaar';
    if ((alt.machine.scores.compact - best.machine.scores.compact) >= 1.8) return 'Compacter';
    if ((alt.machine.scores.milk - best.machine.scores.milk) >= 1.2) return 'Sterker voor melkdranken';
    if (alt.machine.rotary && !best.machine.rotary) return 'Stillere pomp';
    return 'Andere sterke match';
  }

  function altDescription(best, alt, answers) {
    const label = tradeoffLabel(best, alt);
    const sentences = {
      'Voordeliger alternatief': `Een logische keuze als je minder wilt uitgeven zonder te ver van je belangrijkste voorkeuren af te wijken.`,
      'Meer gebruiksgemak': `Past beter als je gemak en herhaalbaarheid uiteindelijk zwaarder wilt laten wegen.`,
      'Meer controle': `Interessant als je nog meer wilt experimenteren met je espresso-instellingen.`,
      'Sneller klaar': `Een sterke optie als korte opwarmtijd in de praktijk extra belangrijk voor je is.`,
      'Compacter': `Handig als werkbladruimte uiteindelijk zwaarder weegt dan bij je eerste keuze.`,
      'Sterker voor melkdranken': `Kijk hiernaar als je vaker meerdere cappuccino’s of flat whites achter elkaar gaat maken.`,
      'Stillere pomp': `Interessant als geluid in dagelijks gebruik belangrijker blijkt dan bij je eerste keuze.`,
      'Andere sterke match': `Scoort eveneens goed op jouw combinatie van koffiestijl, workflow en budget.`
    };
    return sentences[label] || sentences['Andere sterke match'];
  }

  function formatAvailability(product) {
    if (product.available === false) return { text: 'Momenteel niet beschikbaar', cls: 'is-unavailable' };
    if (product.available === true) return { text: 'Beschikbaar', cls: '' };
    return { text: 'Bekijk de actuele beschikbaarheid op de productpagina', cls: 'is-unavailable' };
  }

  function comparisonRows(results, withGrinder) {
    const rows = [
      { label: 'Machineprijs', values: results.map((r) => money(r.machine.price || r.machine.fallbackPrice)), always: true },
      ...(withGrinder ? [{ label: 'Budgetindicatie complete setup', values: results.map((r) => money(r.score.total)), always: true }] : []),
      { label: 'Boilersysteem', values: results.map((r) => r.machine.boiler) },
      { label: 'Pomp', values: results.map((r) => r.machine.pump) },
      { label: 'Temperatuurregeling', values: results.map((r) => r.machine.pid ? (r.machine.pidLabel || 'PID') : (r.machine.pidLabel || 'Niet instelbaar')) },
      { label: 'Shot timer', values: results.map((r) => r.machine.shotTimer ? 'Ja' : 'Nee') },
      { label: 'Vaste wateraansluiting', values: results.map((r) => r.machine.fixedWater ? 'Ja' : 'Nee') },
      { label: 'Waterflow instelbaar', values: results.map((r) => r.machine.flowControl ? 'Ja' : 'Nee') },
      { label: 'Programmeerbare dosering', values: results.map((r) => r.machine.volumetric ? 'Ja' : 'Nee') },
      { label: 'Automatisch stoppen op gewicht', values: results.map((r) => r.machine.weightBased ? 'Ja' : 'Nee') },
      { label: 'Breedte', values: results.map((r) => r.machine.width ? `${(r.machine.width / 10).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} cm` : '—') },
      { label: 'Espresso zetten én melk opschuimen', values: results.map((r) => r.machine.simultaneous ? 'Tegelijk mogelijk' : 'Na elkaar') }
    ];
    return rows.filter((row) => row.always || new Set(row.values).size > 1);
  }

  function analytics(event, payload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  }

  function profileProperties(answers, results, consent) {
    const best = results[0];
    const values = {
      'Espresso Quiz - Completed': true,
      'Espresso Quiz - Current Setup': LABELS.currentSetup[answers.currentSetup] || answers.currentSetup,
      'Espresso Quiz - Coffee Preference': LABELS.drinks[answers.drinks] || answers.drinks,
      'Espresso Quiz - Back To Back Usage': LABELS.batch[answers.batch] || answers.batch,
      'Espresso Quiz - Workflow Preference': LABELS.workflow[answers.workflow] || answers.workflow,
      'Espresso Quiz - Priorities': (answers.priorities || []).map((p) => LABELS.priorities[p] || p),
      'Espresso Quiz - Width Requirement': LABELS.width[answers.width] || answers.width || 'Niet opgegeven',
      'Espresso Quiz - Ease Preference': LABELS.automation[answers.automation] || answers.automation,
      'Espresso Quiz - Grinder Preference': LABELS.grinder[answers.grinder] || answers.grinder || 'Niet nodig',
      'Espresso Quiz - Budget': Number(answers.budget) >= 90000 ? 'Open budget' : `Tot ${money(Number(answers.budget))}`,
      'Espresso Quiz - Best Match': best ? `${best.machine.brand} ${best.machine.name}` : '',
      'Espresso Quiz - Recommendation 2': results[1] ? `${results[1].machine.brand} ${results[1].machine.name}` : '',
      'Espresso Quiz - Recommendation 3': results[2] ? `${results[2].machine.brand} ${results[2].machine.name}` : '',
      'Espresso Quiz - Recommended Grinder': best && best.grinderChoice ? `${best.grinderChoice.grinder.brand} ${best.grinderChoice.grinder.name}` : 'Niet nodig',
      'Espresso Quiz - Match Score': best ? best.score.matchPercent : null,
      'Espresso Quiz - Marketing Consent': Boolean(consent),
      'Espresso Quiz - Completed At': new Date().toISOString()
    };
    return values;
  }

  function klaviyoDebug(root, ...args) {
    if (root.dataset.showDebug === 'true') console.info('[CasaBarista finder]', ...args);
  }

  function klaviyoWarn(root, ...args) {
    if (root.dataset.showDebug === 'true') console.warn('[CasaBarista finder]', ...args);
  }

  function getKlaviyoConfig(root) {
    return {
      publicKey: (root.dataset.klaviyoPublicKey || '').trim(),
      listId: (root.dataset.klaviyoListId || '').trim()
    };
  }

  function ensureKlaviyoProxy() {
    if (window.klaviyo) return window.klaviyo;
    window._klOnsite = window._klOnsite || [];
    try {
      window.klaviyo = new Proxy({}, {
        get(_target, method) {
          if (method === 'push') {
            return (...args) => window._klOnsite.push(...args);
          }
          return (...args) => {
            const callback = typeof args[args.length - 1] === 'function' ? args.pop() : null;
            return new Promise((resolve) => {
              window._klOnsite.push([method, ...args, (result) => {
                if (callback) callback(result);
                resolve(result);
              }]);
            });
          };
        }
      });
    } catch (_error) {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push = (...args) => window._klOnsite.push(...args);
    }
    return window.klaviyo;
  }

  function loadKlaviyoOnsite(root) {
    const { publicKey } = getKlaviyoConfig(root);
    if (!publicKey) return Promise.resolve(false);

    ensureKlaviyoProxy();
    const exactSrc = `https://static.klaviyo.com/onsite/js/${encodeURIComponent(publicKey)}/klaviyo.js`;
    const existing = Array.from(document.scripts).find((script) => {
      const src = script.src || '';
      return src.includes('static.klaviyo.com/onsite/js/') && (src.includes(`/${publicKey}/klaviyo.js`) || src.includes(`company_id=${publicKey}`));
    });
    if (existing) {
      klaviyoDebug(root, 'Klaviyo onsite script already present.');
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = exactSrc;
      script.dataset.cbfinderKlaviyo = 'true';
      script.addEventListener('load', () => {
        klaviyoDebug(root, 'Klaviyo onsite script loaded.');
        resolve(true);
      }, { once: true });
      script.addEventListener('error', () => {
        klaviyoWarn(root, 'Klaviyo onsite script could not be loaded. Client API tracking will still be attempted.');
        resolve(false);
      }, { once: true });
      document.head.appendChild(script);
    });
  }

  function eventUniqueId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return `cbfinder_${window.crypto.randomUUID()}`;
    return `cbfinder_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }

  async function createKlaviyoClientEvent(root, publicKey, email, props, answers, results) {
    const payload = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            ...props,
            'Quiz Answers': { ...answers },
            'Best Match URL': results[0] ? results[0].machine.sourceUrl : '',
            'Recommended Setup Total': results[0] ? results[0].score.total : null,
            'Finder Source': 'CasaBarista Espressomachine Keuzehulp'
          },
          time: new Date().toISOString(),
          unique_id: eventUniqueId(),
          metric: {
            data: {
              type: 'metric',
              attributes: { name: 'Completed Espresso Machine Finder' }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: props
              }
            }
          }
        }
      }
    };

    const response = await fetch(`https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(publicKey)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Klaviyo event failed (${response.status})${body ? `: ${body}` : ''}`);
    }
    klaviyoDebug(root, 'Klaviyo event accepted (202).');
    return true;
  }

  async function createKlaviyoSubscription(root, publicKey, listId, email, props) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: props,
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } }
                }
              }
            }
          },
          custom_source: 'CasaBarista Espressomachine Keuzehulp'
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicKey)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Klaviyo subscription failed (${response.status})${body ? `: ${body}` : ''}`);
    }
    klaviyoDebug(root, 'Klaviyo marketing subscription accepted (202).');
    return true;
  }

  async function identifyWithKlaviyoOnsite(root, email, props) {
    const { publicKey } = getKlaviyoConfig(root);
    if (!publicKey) return false;
    await loadKlaviyoOnsite(root);
    const klaviyo = ensureKlaviyoProxy();
    if (!klaviyo || typeof klaviyo.identify !== 'function') return false;

    try {
      // Best effort: this cookies/identifies the shopper for future onsite behaviour.
      // The authoritative event/profile write is performed via the Client Events API below.
      const identifyPromise = klaviyo.identify({ email, ...props });
      await Promise.race([
        Promise.resolve(identifyPromise),
        new Promise((resolve) => setTimeout(resolve, 2500))
      ]);
      klaviyoDebug(root, 'Klaviyo onsite identify queued/completed.');
      return true;
    } catch (error) {
      klaviyoWarn(root, 'Klaviyo onsite identify failed; Client API tracking continues.', error);
      return false;
    }
  }

  async function sendKlaviyo(root, email, answers, results, consent) {
    const props = profileProperties(answers, results, consent);
    const { publicKey, listId } = getKlaviyoConfig(root);

    if (!publicKey) {
      klaviyoWarn(root, 'No Klaviyo Public API Key / Company ID configured. No Klaviyo profile or event can be sent.');
      return { event: false, identify: false, subscription: false, reason: 'missing_public_key' };
    }

    // Do not depend on the Shopify/Klaviyo app embed being active in a theme preview.
    // The Client Events API creates/updates the profile properties and records the custom event directly.
    let eventSent = false;
    try {
      eventSent = await createKlaviyoClientEvent(root, publicKey, email, props, answers, results);
    } catch (error) {
      klaviyoWarn(root, 'Klaviyo event/profile request failed.', error);
    }

    // Best effort so subsequent onsite activity can be tied to the same Klaviyo profile.
    const identifyPromise = identifyWithKlaviyoOnsite(root, email, props);

    let subscriptionSent = false;
    if (consent) {
      if (!listId) {
        klaviyoWarn(root, 'Marketing consent was checked, but no Klaviyo List ID is configured.');
      } else {
        try {
          subscriptionSent = await createKlaviyoSubscription(root, publicKey, listId, email, props);
        } catch (error) {
          klaviyoWarn(root, 'Klaviyo marketing subscription request failed.', error);
        }
      }
    }

    const identifySent = await identifyPromise;
    return { event: eventSent, identify: identifySent, subscription: subscriptionSent };
  }

  function renderQuestion(root, state) {
    const sequence = questionSequence(state.answers);
    state.sequence = sequence;
    state.index = clamp(state.index, 0, sequence.length - 1);
    const question = sequence[state.index];
    const container = root.querySelector('[data-question-container]');
    const current = state.answers[question.id];
    const selected = question.multi ? (Array.isArray(current) ? current : []) : current;
    const progress = ((state.index + 1) / sequence.length) * 100;
    root.querySelector('[data-progress-label]').textContent = `Vraag ${state.index + 1} van ${sequence.length}`;
    root.querySelector('[data-progress-percent]').textContent = `${Math.round(progress)}%`;
    root.querySelector('[data-progress-bar]').style.width = `${progress}%`;

    container.innerHTML = `
      <div class="cbfinder__question-card">
        <h2 class="cbfinder__question-title">${escapeHtml(question.title)}</h2>
        ${question.help ? `<p class="cbfinder__question-help">${escapeHtml(question.help)}</p>` : ''}
        <div class="cbfinder__options" role="${question.multi ? 'group' : 'radiogroup'}" aria-label="${escapeHtml(question.title)}">
          ${question.options.map(([value, title, desc]) => {
            const isSelected = question.multi ? selected.includes(value) : selected === value;
            return `<button class="cbfinder__option ${question.multi ? 'cbfinder__option--multi' : 'cbfinder__option--single'}${isSelected ? ' is-selected' : ''}" type="button" data-option="${escapeHtml(value)}" aria-pressed="${isSelected ? 'true' : 'false'}">
              <span class="cbfinder__option-check" aria-hidden="true">${question.multi ? (isSelected ? '✓' : '') : (isSelected ? '✓' : '›')}</span>
              <span class="cbfinder__option-title">${escapeHtml(title)}</span>
              ${desc ? `<span class="cbfinder__option-desc">${escapeHtml(desc)}</span>` : ''}
            </button>`;
          }).join('')}
        </div>
        ${question.multi ? `<p class="cbfinder__multi-note">Kies maximaal ${question.max}. <span data-multi-count>${selected.length}</span> geselecteerd.</p>` : ''}
        <div class="cbfinder__question-actions">
          <button class="cbfinder__back-link" type="button" data-question-back>${state.index === 0 ? '← Terug naar start' : '← Vorige vraag'}</button>
          ${question.multi ? `<button class="btn btn--primary" type="button" data-question-next ${selected.length ? '' : 'disabled'}>Verder</button>` : ''}
        </div>
      </div>`;

    container.querySelectorAll('[data-option]').forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.option;
        if (question.multi) {
          const arr = Array.isArray(state.answers[question.id]) ? [...state.answers[question.id]] : [];
          const idx = arr.indexOf(value);
          if (idx >= 0) arr.splice(idx, 1);
          else if (arr.length < question.max) arr.push(value);
          else return;
          state.answers[question.id] = arr;
          saveAnswers(state.answers);
          renderQuestion(root, state);
          return;
        }
        state.answers[question.id] = value;
        if (question.id === 'currentSetup' && !needsGrinder(state.answers)) delete state.answers.grinder;
        if (question.id === 'priorities' && !(state.answers.priorities || []).includes('compact')) delete state.answers.width;
        saveAnswers(state.answers);
        analytics('espresso_finder_answered', { question: question.id, answer: value });
        setTimeout(() => advanceQuestion(root, state), 140);
      });
    });

    const next = container.querySelector('[data-question-next]');
    if (next) next.addEventListener('click', () => {
      analytics('espresso_finder_answered', { question: question.id, answer: state.answers[question.id] });
      advanceQuestion(root, state);
    });
    container.querySelector('[data-question-back]').addEventListener('click', () => goBack(root, state));
  }

  function showView(root, view) {
    root.querySelectorAll('[data-view]').forEach((el) => { el.hidden = el.dataset.view !== view; });
    const target = root.querySelector(`[data-view="${view}"]`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function advanceQuestion(root, state) {
    const sequence = questionSequence(state.answers);
    if (state.index >= sequence.length - 1) {
      showView(root, 'gate');
      analytics('espresso_finder_email_gate_viewed');
      return;
    }
    state.index += 1;
    renderQuestion(root, state);
  }

  function goBack(root, state) {
    if (state.index <= 0) {
      showView(root, 'intro');
      return;
    }
    state.index -= 1;
    renderQuestion(root, state);
  }

  function findResumeIndex(answers) {
    const seq = questionSequence(answers);
    const idx = seq.findIndex((q) => {
      const value = answers[q.id];
      return q.multi ? !Array.isArray(value) || value.length === 0 : value === undefined || value === null || value === '';
    });
    return idx === -1 ? Math.max(0, seq.length - 1) : idx;
  }

  async function buildResults(root, state) {
    const budgetMax = Number(state.answers.budget || 0) || 99999;
    const accessoryReserve = needsGrinder(state.answers) ? Number(root.dataset.accessoryReserve || 100) : 0;
    const discountRate = grinderDiscountRate(root);
    const machines = await state.machinePromise;
    const grinders = await state.grinderPromise;

    const scored = machines.map((machine) => {
      const grinderChoice = chooseGrinder(machine, grinders, state.answers, budgetMax, accessoryReserve, discountRate);
      const score = scoreMachine(machine, state.answers, grinderChoice, budgetMax, accessoryReserve);
      return { machine, grinderChoice, score, grinder: grinderChoice ? grinderChoice.grinder : null };
    }).filter((item) => !item.score.excluded);

    const available = scored.filter((item) => item.machine.available !== false).sort((a, b) => b.score.score - a.score.score);
    const unavailable = scored.filter((item) => item.machine.available === false).sort((a, b) => b.score.score - a.score.score);
    let results = [...available, ...unavailable].slice(0, 3);

    // If an alternative is extremely far over budget and enough closer options exist, skip it.
    if (budgetMax < 90000) {
      const sensible = [...available, ...unavailable].filter((item) => item.score.total <= budgetMax * 1.20);
      if (sensible.length >= 2) results = sensible.slice(0, 3);
    }

    if (!results.length) {
      // Safety fallback: never invent a product; show the best currently present catalog products even if a hard preference was too strict.
      results = machines.filter((m) => m.exists !== false).slice(0, 2).map((machine) => {
        const grinderChoice = chooseGrinder(machine, grinders, state.answers, budgetMax, accessoryReserve, discountRate);
        return { machine, grinderChoice, grinder: grinderChoice ? grinderChoice.grinder : null, score: scoreMachine(machine, { ...state.answers, priorities: (state.answers.priorities || []).filter((p) => p !== 'fixedWater'), width: 'unlimited' }, grinderChoice, budgetMax, accessoryReserve) };
      });
    }

    state.results = results;
    return results;
  }

  function imageHtml(product, alt) {
    return product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(alt)}" loading="lazy" width="600" height="600">`
      : `<div aria-hidden="true" style="font-size:56px;opacity:.22">☕</div>`;
  }

  function resultProductUrl(product) {
    return product.url && product.url.startsWith('/') ? product.url : `/products/${product.handle}`;
  }

  function purchasableVariants(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const available = variants.filter((variant) => variant.available !== false && variant.id);
    return available.length ? available : variants.filter((variant) => variant.id);
  }

  function variantPickerHtml(product, role, label) {
    const variants = purchasableVariants(product);
    if (!variants.length) return '';
    const first = variants[0];
    if (variants.length === 1 || (variants.length === 1 && first.title === 'Default Title')) {
      return `<input type="hidden" data-set-variant="${escapeHtml(role)}" value="${first.id}" data-variant-price="${first.price || product.price || product.fallbackPrice || 0}">`;
    }
    return `<label class="cbfinder__variant-field">
      <span>${escapeHtml(label)}</span>
      <select class="cbfinder__variant-select" data-set-variant="${escapeHtml(role)}">
        ${variants.map((variant) => `<option value="${variant.id}" data-price="${variant.price || product.price || product.fallbackPrice || 0}">${escapeHtml(variant.title)}${variant.price ? ` · ${money(variant.price)}` : ''}</option>`).join('')}
      </select>
    </label>`;
  }

  function selectedVariant(container, role, product) {
    const control = container.querySelector(`[data-set-variant="${role}"]`);
    if (!control) return null;
    if (control.tagName === 'SELECT') {
      const option = control.options[control.selectedIndex];
      return { id: Number(control.value), price: Number(option?.dataset?.price || product.price || product.fallbackPrice || 0) };
    }
    return { id: Number(control.value), price: Number(control.dataset.variantPrice || product.price || product.fallbackPrice || 0) };
  }

  function updateSetPricing(root, box, machine, grinder) {
    if (!box || !machine) return;
    const machineVariant = selectedVariant(box, 'machine', machine);
    const grinderVariant = grinder ? selectedVariant(box, 'grinder', grinder) : null;
    const machinePrice = machineVariant?.price || machine.price || machine.fallbackPrice || 0;
    const discountRate = grinderDiscountRate(root);
    const grinderRegular = grinderVariant?.price || grinder?.price || grinder?.fallbackPrice || 0;
    const grinderDiscounted = grinder ? grinderRegular * (1 - discountRate) : 0;
    const savings = grinder ? grinderRegular - grinderDiscounted : 0;
    const total = machinePrice + grinderDiscounted;

    const setText = (selector, value) => {
      const el = box.querySelector(selector);
      if (el) el.textContent = value;
    };
    setText('[data-set-machine-price]', money(machinePrice));
    if (grinder) {
      setText('[data-set-grinder-regular]', money(grinderRegular));
      setText('[data-set-grinder-discounted]', money(grinderDiscounted));
      setText('[data-set-savings]', money(savings));
    }
    setText('[data-set-total]', money(total));
  }

  async function addRecommendationToCart(root, box, machine, grinder, button) {
    const machineVariant = selectedVariant(box, 'machine', machine);
    const grinderVariant = grinder ? selectedVariant(box, 'grinder', grinder) : null;
    const status = box.querySelector('[data-set-status]');
    if (!machineVariant?.id || (grinder && !grinderVariant?.id)) {
      if (status) {
        status.hidden = false;
        status.textContent = 'Kies eerst een beschikbare uitvoering.';
      }
      return;
    }

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Toevoegen…';
    if (status) status.hidden = true;

    try {
      const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const items = [{ id: machineVariant.id, quantity: 1 }];
      if (grinderVariant?.id) items.push({ id: grinderVariant.id, quantity: 1 });
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) {
        let message = 'Toevoegen aan de winkelmand lukt nu niet. Probeer het opnieuw.';
        try {
          const error = await response.json();
          if (error?.description) message = error.description;
        } catch (_) {}
        throw new Error(message);
      }

      analytics(grinder ? 'espresso_finder_set_added_to_cart' : 'espresso_finder_machine_added_to_cart', {
        recommended_product: `${machine.brand} ${machine.name}`,
        recommended_grinder: grinder ? `${grinder.brand} ${grinder.name}` : '',
        machine_variant_id: machineVariant.id,
        grinder_variant_id: grinderVariant?.id || ''
      });

      // Open the actual Shopify cart so the customer immediately sees the live automatic discount and totals.
      window.location.href = `${shopRoot}cart`;
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      if (status) {
        status.hidden = false;
        status.textContent = error?.message || 'Toevoegen aan de winkelmand lukt nu niet. Probeer het opnieuw.';
      }
    }
  }

  function renderResults(root, state, results) {
    const container = root.querySelector('[data-results-container]');
    if (!results.length) {
      container.innerHTML = `<div class="cbfinder__question-card"><h2>We hebben geen perfecte match gevonden</h2><p>Je wensen vallen net buiten de combinaties die we goed kunnen adviseren. Bekijk alle espressomachines of neem contact met ons op, dan denken we persoonlijk met je mee.</p><a class="btn btn--primary" href="/collections/espressomachines">Bekijk alle espressomachines</a></div>`;
      showView(root, 'results');
      return;
    }

    const best = results[0];
    const bestAvailability = formatAvailability(best.machine);
    const budgetMax = Number(state.answers.budget || 99999);
    const bestReasons = machineReasonCandidates(best.machine, state.answers, best.score, best.grinderChoice, budgetMax);
    const withGrinder = needsGrinder(state.answers);
    const rows = comparisonRows(results, withGrinder);

    const discountPercent = Math.round(grinderDiscountRate(root) * 100);
    const bestMachineVariants = purchasableVariants(best.machine);
    const bestGrinderVariants = withGrinder && best.grinder ? purchasableVariants(best.grinder) : [];
    const canAddMachine = best.machine.available !== false && bestMachineVariants.length > 0;
    const canAddSet = canAddMachine && (!withGrinder || (best.grinder && best.grinder.available !== false && bestGrinderVariants.length > 0));
    const machinePriceForSet = bestMachineVariants[0]?.price || best.machine.price || best.machine.fallbackPrice || 0;
    const grinderRegularForSet = bestGrinderVariants[0]?.price || best.grinder?.price || best.grinder?.fallbackPrice || 0;
    const grinderDiscountedForSet = best.grinder ? discountedGrinderPrice(root, grinderRegularForSet) : 0;
    const setSavings = best.grinder ? grinderRegularForSet - grinderDiscountedForSet : 0;
    const cartSetTotal = machinePriceForSet + grinderDiscountedForSet;

    const grinderBox = withGrinder && best.grinder ? `
      <div class="cbfinder__setup-box" data-set-box>
        <div class="cbfinder__setup-heading-row">
          <span class="cbfinder__setup-label">Jouw aanbevolen combinatie</span>
          <span class="cbfinder__discount-pill">${discountPercent}% korting op de molen</span>
        </div>
        <div class="cbfinder__setup-line"><span>${escapeHtml(best.machine.brand)} ${escapeHtml(best.machine.name)}</span><strong data-set-machine-price>${money(machinePriceForSet)}</strong></div>
        <div class="cbfinder__setup-line cbfinder__setup-line--discount">
          <span>${escapeHtml(best.grinder.brand)} ${escapeHtml(best.grinder.name)}</span>
          <span class="cbfinder__discount-price"><s data-set-grinder-regular>${money(grinderRegularForSet)}</s><strong data-set-grinder-discounted>${money(grinderDiscountedForSet)}</strong></span>
        </div>
        <div class="cbfinder__setup-line cbfinder__setup-total"><span>Machine + molen</span><strong data-set-total>${money(cartSetTotal)}</strong></div>
        <div class="cbfinder__saving">Je bespaart <strong data-set-savings>${money(setSavings)}</strong> op de molen.</div>
        <p class="cbfinder__setup-note">De huidige web-only deal geeft ${discountPercent}% korting op de koffiemolen wanneer je tegelijk een espressomachine koopt. In je winkelmand zie je de actuele korting en het definitieve totaal.</p>
        ${Number(root.dataset.accessoryReserve || 0) > 0 ? `<p class="cbfinder__setup-note">Houd daarnaast rekening met ongeveer ${money(Number(root.dataset.accessoryReserve || 0))} voor eventuele basisaccessoires. Die worden niet automatisch toegevoegd.</p>` : ''}
        <div class="cbfinder__variant-grid">
          ${variantPickerHtml(best.machine, 'machine', 'Uitvoering machine')}
          ${variantPickerHtml(best.grinder, 'grinder', 'Uitvoering molen')}
        </div>
        <p class="cbfinder__alt-desc">${escapeHtml(grinderReason(best.grinder, state.answers))}</p>
        ${canAddSet ? `<button class="btn btn--primary cbfinder__add-set" type="button" data-add-set>Voeg machine + molen toe aan winkelmand</button>` : `<p class="cbfinder__set-unavailable">Deze combinatie kan nu niet direct worden toegevoegd. Bekijk de producten voor de actuele beschikbaarheid.</p>`}
        <p class="cbfinder__set-status" data-set-status hidden></p>
        <div class="cbfinder__setup-links">
          <a class="cbfinder__alt-link" href="${escapeHtml(resultProductUrl(best.machine))}" data-product-click data-product-name="${escapeHtml(best.machine.brand + ' ' + best.machine.name)}">Bekijk machine</a>
          <a class="cbfinder__alt-link" href="${escapeHtml(resultProductUrl(best.grinder))}" data-product-click data-product-name="${escapeHtml(best.grinder.brand + ' ' + best.grinder.name)}">Bekijk molen</a>
        </div>
      </div>` : '';

    const machineOnlyBox = !withGrinder ? `
      <div class="cbfinder__setup-box cbfinder__setup-box--machine" data-set-box>
        <span class="cbfinder__setup-label">Jouw aanbevolen machine</span>
        ${variantPickerHtml(best.machine, 'machine', 'Uitvoering')}
        ${canAddMachine ? `<button class="btn btn--primary cbfinder__add-set" type="button" data-add-set>Voeg deze machine toe aan winkelmand</button>` : `<p class="cbfinder__set-unavailable">Deze machine kan nu niet direct worden toegevoegd. Bekijk de productpagina voor de actuele beschikbaarheid.</p>`}
        <p class="cbfinder__set-status" data-set-status hidden></p>
      </div>` : '';

    container.innerHTML = `
      <div class="cbfinder__results-head">
        <div class="cbfinder__eyebrow">Jouw persoonlijke advies</div>
        <h2 class="cbfinder__results-title">Dit zijn jouw beste matches</h2>
        <p class="cbfinder__results-sub">Op basis van wat jij belangrijk vindt, sluiten deze machines het beste aan. Vergelijk de belangrijkste verschillen en ontdek welke het beste bij jouw manier van koffiezetten past.</p>
      </div>

      <article class="cbfinder__best">
        <div class="cbfinder__product-media">
          <span class="cbfinder__match-badge">${best.score.matchPercent}% match</span>
          ${imageHtml(best.machine, `${best.machine.brand} ${best.machine.name}`)}
        </div>
        <div class="cbfinder__best-body">
          <div class="cbfinder__kicker">Onze beste match voor jou</div>
          <h3 class="cbfinder__product-title">${escapeHtml(best.machine.brand)} ${escapeHtml(best.machine.name)}</h3>
          <div class="cbfinder__price">${money(best.machine.price || best.machine.fallbackPrice)}</div>
          <div class="cbfinder__availability ${bestAvailability.cls}">${escapeHtml(bestAvailability.text)}</div>
          <ul class="cbfinder__reasons">${bestReasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join('')}</ul>
          ${grinderBox}
          ${machineOnlyBox}
          <div class="cbfinder__product-actions">
            <a class="btn btn--secondary" href="${escapeHtml(resultProductUrl(best.machine))}" data-product-click data-product-name="${escapeHtml(best.machine.brand + ' ' + best.machine.name)}">Bekijk deze machine</a>
            ${results.length > 1 ? `<button class="btn btn--secondary" type="button" data-scroll-compare>Vergelijk de ${results.length} matches</button>` : ''}
          </div>
        </div>
      </article>

      ${results.length > 1 ? `<section class="cbfinder__alternatives">
        <h3 class="cbfinder__section-title">Ook interessant voor jou</h3>
        <div class="cbfinder__alt-grid">
          ${results.slice(1).map((alt) => {
            const label = tradeoffLabel(best, alt);
            return `<article class="cbfinder__alt-card">
              <div class="cbfinder__alt-image">${imageHtml(alt.machine, `${alt.machine.brand} ${alt.machine.name}`)}</div>
              <div class="cbfinder__alt-body">
                <span class="cbfinder__tradeoff">${escapeHtml(label)}</span>
                <h4 class="cbfinder__alt-title">${escapeHtml(alt.machine.brand)} ${escapeHtml(alt.machine.name)}</h4>
                <div class="cbfinder__price">${money(alt.machine.price || alt.machine.fallbackPrice)} · ${alt.score.matchPercent}% match</div>
                <p class="cbfinder__alt-desc">${escapeHtml(altDescription(best, alt, state.answers))}</p>
                ${withGrinder && alt.grinder ? `<p class="cbfinder__alt-desc"><strong>Molen:</strong> ${escapeHtml(alt.grinder.brand)} ${escapeHtml(alt.grinder.name)} · totaal ca. ${money(alt.score.total)}</p>` : ''}
                <a class="cbfinder__alt-link" href="${escapeHtml(resultProductUrl(alt.machine))}" data-product-click data-product-name="${escapeHtml(alt.machine.brand + ' ' + alt.machine.name)}">Bekijk machine</a>
              </div>
            </article>`;
          }).join('')}
        </div>
      </section>` : ''}

      ${results.length > 1 ? `<section class="cbfinder__comparison" data-comparison>
        <h3 class="cbfinder__section-title">Vergelijk jouw matches</h3>
        <div class="cbfinder__table-wrap">
          <table class="cbfinder__table">
            <thead><tr><th>Eigenschap</th>${results.map((r) => `<th>${escapeHtml(r.machine.brand)} ${escapeHtml(r.machine.name)}</th>`).join('')}</tr></thead>
            <tbody>${rows.map((row) => `<tr><th>${escapeHtml(row.label)}</th>${row.values.map((value) => `<td>${escapeHtml(value)}</td>`).join('')}</tr>`).join('')}</tbody>
          </table>
        </div>
      </section>` : ''}

      <div class="cbfinder__restart">
        <button class="cbfinder__back-link" type="button" data-restart>↻ Keuzehulp opnieuw doen</button>

      </div>`;

    container.querySelectorAll('[data-product-click]').forEach((link) => {
      link.addEventListener('click', () => analytics('espresso_finder_product_clicked', { recommended_product: link.dataset.productName || '' }));
    });
    const setBox = container.querySelector('[data-set-box]');
    if (setBox) {
      setBox.querySelectorAll('[data-set-variant]').forEach((control) => {
        if (control.tagName === 'SELECT') control.addEventListener('change', () => updateSetPricing(root, setBox, best.machine, withGrinder ? best.grinder : null));
      });
      updateSetPricing(root, setBox, best.machine, withGrinder ? best.grinder : null);
      const addButton = setBox.querySelector('[data-add-set]');
      if (addButton) addButton.addEventListener('click', () => addRecommendationToCart(root, setBox, best.machine, withGrinder ? best.grinder : null, addButton));
    }
    const compareButton = container.querySelector('[data-scroll-compare]');
    if (compareButton) compareButton.addEventListener('click', () => container.querySelector('[data-comparison]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    container.querySelector('[data-restart]').addEventListener('click', () => {
      state.answers = {};
      state.index = 0;
      state.results = [];
      saveAnswers(state.answers);
      showView(root, 'intro');
    });

    showView(root, 'results');
  }

  function setupFinder(root) {
    if (root.dataset.cbfinderInitialized === 'true') return;
    root.dataset.cbfinderInitialized = 'true';

    // Prime Klaviyo in previews as well; tracking itself also has a direct Client API fallback.
    loadKlaviyoOnsite(root);

    const state = {
      answers: loadSavedAnswers(),
      index: 0,
      sequence: [],
      results: [],
      machinePromise: Promise.all(MACHINES.map((m) => fetchShopifyProduct(m.handle, m))),
      grinderPromise: Promise.all(GRINDERS.map((g) => fetchShopifyProduct(g.handle, { ...g, verifiedAt: VERIFIED_AT })))
    };

    root.querySelector('[data-action="start"]').addEventListener('click', () => {
      state.index = findResumeIndex(state.answers);
      showView(root, 'quiz');
      renderQuestion(root, state);
      analytics('espresso_finder_started');
    });

    root.querySelector('[data-action="back-from-gate"]').addEventListener('click', () => {
      state.index = Math.max(0, questionSequence(state.answers).length - 1);
      showView(root, 'quiz');
      renderQuestion(root, state);
    });

    const form = root.querySelector('[data-email-form]');
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const emailInput = root.querySelector('[data-email-input]');
      const error = root.querySelector('[data-email-error]');
      const consent = root.querySelector('[data-marketing-consent]').checked;
      const email = emailInput.value.trim();
      if (!emailInput.checkValidity() || !email) {
        error.hidden = false;
        emailInput.focus();
        return;
      }
      error.hidden = true;
      const button = root.querySelector('[data-submit-button]');
      button.disabled = true;
      root.querySelector('[data-submit-spinner]').hidden = false;
      analytics('espresso_finder_email_submitted', { marketing_consent: consent });

      try {
        const results = await buildResults(root, state);
        renderResults(root, state, results);
        analytics('espresso_finder_completed', {
          recommended_product: results[0] ? `${results[0].machine.brand} ${results[0].machine.name}` : '',
          match_score: results[0] ? results[0].score.matchPercent : null
        });
        void sendKlaviyo(root, email, state.answers, results, consent).catch((error) => {
          klaviyoWarn(root, 'Unexpected Klaviyo integration error.', error);
        }); // Deliberately non-blocking for UX.
      } catch (err) {
        if (root.dataset.showDebug === 'true') console.error('[CasaBarista finder] Could not build results', err);
        error.textContent = 'Er ging iets mis bij het berekenen van je advies. Probeer het nog een keer.';
        error.hidden = false;
      } finally {
        button.disabled = false;
        root.querySelector('[data-submit-spinner]').hidden = true;
      }
    });
  }

  const init = () => document.querySelectorAll('[data-cbfinder-root]').forEach(setupFinder);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
